import React, { useState, useEffect, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams, Navigate } from 'react-router-dom';
import { usePortfolioData } from '../../components/data/PortfolioData';
import SidebarPageLayout from '../../components/layout/SidebarPageLayout';
import Marquee from '../../components/layout/Marquee';
import Tooltip from '../../components/layout/Tooltip';
import ChartTooltip from '../../components/layout/ChartTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBatteryFull, faSolarPanel, faBoltLightning, faBolt, faSun, faTemperatureHigh, faWind, faInfoCircle, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import inverterIcon from '../../img/inverter-icon.svg';
import dcdcConnverterIcon from '../../img/dcdc-converter-icon.svg';
import MultiLineChart from '../../components/graphs/MultiLineChart';
import SingleBarChart from '../../components/graphs/SingleBarChart';
import TimescaleTabs, { DASHBOARD_TIMESCALE_TABS } from '../../components/layout/TimescaleTabs';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { createPortfolioDateFormatter } from '../../utils/dateTime';
import { formatTemperature } from '../../util/temperature';
import { useTemperatureUnit } from '../../util/useTemperatureUnit';

// Configuration for the single-line hourly charts (24hr / 48hr tabs).
// Each entry is rendered with the MultiLineChart component; the dashboard
// only picks the duration based on the active tab and builds the endpoint.
const SINGLE_LINE_SERIES = [
  {
    key: 'grid',
    pathSuffix: 'grid',
    color: '#0077cc',
    domain: [-100, 700],
    tickValues: [700, 0, -100],
    midlines: [
      { value: 350, label: '350 MW' },
    ],
    wrapperClassName: 'grid mt-3',
    title: 'GRID',
  },
  {
    key: 'solar',
    pathSuffix: 'solar',
    color: '#D07F15',
    domain: [0, 1000],
    tickValues: [1000, 0],
    midlines: [{ value: 500, label: '500 MW' }],
    wrapperClassName: 'solar mt-3',
    title: 'SOLAR',
  },
  {
    key: 'storage',
    pathSuffix: 'storage',
    color: '#008900',
    domain: [-300, 300],
    tickValues: [300, 0, -300],
    midlines: [
      { value: 150, label: '150 MW' },
      { value: -150, label: '-150 MW' },
    ],
    wrapperClassName: 'storage mt-3 mb-4',
    title: 'STORAGE',
  },
];

// Configuration for the single-bar daily charts (week / month tabs).
// Diverging series (grid, storage) render bars from `pos` to `neg`. Grid uses the
// same asymmetric Y axis as the line charts (-100..700 MWh); storage auto-scales.
const SINGLE_BAR_SERIES = [
  {
    key: 'grid',
    pathSuffix: 'grid',
    variant: 'diverging',
    gradient: { start: '#3b82f6', end: '#000' },
    domain: [-100, 700],
    tickValues: [700, 0, -100],
    wrapperClassName: 'grid mt-3',
    title: 'GRID',
    tooltipLabels: { pos: 'Produced', neg: 'Consumed' },
  },
  {
    key: 'solar',
    pathSuffix: 'solar',
    variant: 'positive',
    gradient: { start: '#D07F15', end: '#000' },
    wrapperClassName: 'solar mt-3',
    title: 'SOLAR',
    tooltipLabels: { value: 'Generated' },
  },
  {
    key: 'storage',
    pathSuffix: 'storage',
    variant: 'diverging',
    gradient: { start: '#008900', end: '#FFE100' },
    wrapperClassName: 'storage mt-3 mb-4',
    title: 'STORAGE',
    tooltipLabels: { pos: 'Charged', neg: 'Discharged' },
  },
];

function normalizePortfolioName(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function formatLineSeriesTotal(value) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function buildLineSeriesHeaderRight(title, total) {
  return (
    <>
      <strong>{title} PEAK</strong> ({formatLineSeriesTotal(total)} MW)
    </>
  );
}

function buildSeriesHeaderLeft(seriesKey, aggregates) {
  if (seriesKey === 'grid') {
    return (
      <>
        <strong>DELIVERED ENERGY:</strong> ({formatLineSeriesTotal(aggregates?.delivered_energy)} MWh)
      </>
    );
  }
  if (seriesKey === 'solar') {
    return (
      <>
        <strong>GENERATED ENERGY:</strong> ({formatLineSeriesTotal(aggregates?.generate_energy)} MWh)
      </>
    );
  }
  if (seriesKey === 'storage') {
    return (
      <>
        <strong>CHARGED:</strong> ({formatLineSeriesTotal(aggregates?.charged)} MWh) /{' '}
        <strong>DISCHARGED:</strong> ({formatLineSeriesTotal(aggregates?.discharged)} MWh)
      </>
    );
  }
  return null;
}

function Dashboard() {
  const temperatureUnit = useTemperatureUnit();
  const { portfolioName = '' } = useParams();
  const { data } = usePortfolioData();
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [activeTab, setActiveTab] = useState(1);
  // When the user picks a complete range in the datepicker this becomes
  // `{ start: Date, end: Date }` and supersedes `activeTab` for the
  // Grid / Solar / Storage line charts.
  const [customRange, setCustomRange] = useState(null);
  const [seriesAggregates, setSeriesAggregates] = useState({});
  const isCustomRangeActive = Boolean(customRange?.start && customRange?.end);
  const formatRangeDate = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };
  // Range that mirrors the rolling window the charts render for each tab:
  //   0: 24hr  → now-24h → now (today + yesterday)
  //   1: 48hr  → now-48h → now (potentially 3 calendar days)
  //   2: week  → 7 days ending today
  const tabRange = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (activeTab === 0) {
      return { start: new Date(now.getTime() - 24 * 60 * 60 * 1000), end: now };
    }
    if (activeTab === 1) {
      return { start: new Date(now.getTime() - 48 * 60 * 60 * 1000), end: now };
    }
    if (activeTab === 2) {
      const start = new Date(todayStart);
      start.setDate(todayStart.getDate() - 6);
      return { start, end: todayStart };
    }
    return null;
  }, [activeTab]);
  const dateRangeLabel = isCustomRangeActive ? (
    <>
      <strong>{formatRangeDate(customRange.start)}</strong> to{' '}
      <strong>{formatRangeDate(customRange.end)}</strong>
    </>
  ) : tabRange ? (
    <>
      <strong>{formatRangeDate(tabRange.start)}</strong> to{' '}
      <strong>{formatRangeDate(tabRange.end)}</strong>
    </>
  ) : null;

  const portfolio = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }
    const normalizedParam = normalizePortfolioName(portfolioName);
    return data.find((node) => normalizePortfolioName(node.portfolio_name) === normalizedParam) || null;
  }, [data, portfolioName]);

  const portfolioTimeZone = portfolio?.timezone;
  const dateFormatter = useMemo(
    () => createPortfolioDateFormatter(portfolioTimeZone),
    [portfolioTimeZone],
  );

  useEffect(() => {
    if (!portfolio) {
      document.title = 'Vivosol Plant Performance Insights Hub | Dashboard';
      return;
    }
    document.title = `Vivosol Plant Performance Insights Hub | Dashboard / ${portfolio.plant_name}`;
  }, [portfolio]);

  useEffect(() => {
    setSeriesAggregates({});
  }, [activeTab, customRange, portfolio?.portfolio_name]);

  const handleSeriesAggregatesLoaded = (seriesKey) => (aggregates) => {
    setSeriesAggregates((prev) => ({ ...prev, [seriesKey]: aggregates }));
  };

  if (!data || data.length === 0) {
    return null;
  }

  if (!portfolio) {
    return <Navigate replace to="/portfolio" />;
  }

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <Marquee />
        <div className="d-flex justify-content-between align-items-start gap-3 mt-3">
          <h1 className="mb-0"><strong>{portfolio.plant_name} :</strong> {portfolio.config} ({portfolio.location})</h1>
          {portfolio.last_updated ? (
            <span className="text-nowrap small pt-1" title="KPI last updated">
              <strong>Last Updated:</strong> {dateFormatter.formatUtc(portfolio.last_updated)}
            </span>
          ) : null}
        </div>
        <Breadcrumbs labels={{ portfolioName: portfolio.plant_name }} />
        <div className="metrics">
          <div><span className="title"><span><img src={inverterIcon} alt="Inverters" />Inverters</span>
              <Tooltip position="left" content="Number of Inverters that are online vs offline.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.inverters_online} / {portfolio.inverters_offline}</div>
          </div>
          <div><span className="title"><span><img src={dcdcConnverterIcon} alt="DCDC Converters" />DCDC Converters</span>
              <Tooltip position="left" content="Number of DCDC Converters that are online vs offline.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.dcdc_converters_online} / {portfolio.dcdc_converters_offline}</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faBatteryFull} style={{ transform: 'rotate(-90deg)' }} />Battery Racks</span>
              <Tooltip position="left" content="Number of Batteries that are online vs offline.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.battery_racks_online} / {portfolio.battery_racks_offline}</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faSolarPanel} />PV Setpoint</span>
              <Tooltip position="left" content="Refers to the production efficiency of the PV panels.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.pv_setpoint} MW</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faBatteryFull} style={{ transform: 'rotate(-90deg)' }} />BESS Setpoint</span>
              <Tooltip position="left" content="Battery Energy Storage System setpoint.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.bess_setpoint} MW</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faBoltLightning} />Curtailment Today</span>
              <Tooltip position="left" content="900 MW AC nameplate minus PV setpoint. Do not use 858 (DCDC count) or 968.1 (DC rating).">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.curtailment_today} MW</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faBolt} />Generation Peak/Total</span>
              <Tooltip position="left" content="Precomputed MAX generation since UTC midnight (ml_kpi_snapshot).">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.plant_generation_peak} MW / {portfolio.plant_generation_total} MWh</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faBolt} />Aux Load Peak/Total</span>
              <Tooltip position="left" content="Peak auxilary load out of total.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.plant_aux_load_peak} MW / {portfolio.plant_aux_load_total} MWh</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faSun} />Irradiance</span>
              <Tooltip position="left" content="Total potential solar energy available.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.irradiance} W/m2</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faTemperatureHigh} />BOM Temperature</span>
              <Tooltip position="left" content="BOM stands for Back of Module temperature.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.bom_temperature != null ? formatTemperature(portfolio.bom_temperature, temperatureUnit, { space: true }) : '—'}</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faTemperatureHigh} />Temperature</span>
              <Tooltip position="left" content="The temperature at the area of the site.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.temp != null ? formatTemperature(portfolio.temp, temperatureUnit, { space: true }) : '—'}</div>
          </div>
          <div><span className="title"><span><FontAwesomeIcon icon={faWind} />Windspeed</span>
              <Tooltip position="left" content="Wind speed at the area of the site.">
                <button><FontAwesomeIcon icon={faInfoCircle} /></button>
              </Tooltip></span>
            <div>{portfolio.wind}</div>
          </div>
        </div>
        <div id="dashboard" className="panel p-3 my-3">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3 align-items-center">
              <TimescaleTabs
                tabs={DASHBOARD_TIMESCALE_TABS}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showDatePicker
                customRange={customRange}
                setCustomRange={setCustomRange}
                maxRangeDays={7}
                limitToLastDays={7}
              />
              <span className="daterange">{dateRangeLabel}</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button className="d-none"><FontAwesomeIcon icon={faMapLocationDot} /></button>
            </div>
          </div>
          <div className="body gap-5 justify-content-between pt-3">
              <ChartTooltip tooltip={tooltip} transition={true} />
              <div className="d-flex align-items-center gap-4">
              </div>
              {isCustomRangeActive &&
                SINGLE_LINE_SERIES.map((series) => {
                  const start = encodeURIComponent(formatRangeDate(customRange.start));
                  const end = encodeURIComponent(formatRangeDate(customRange.end));
                  return (
                    <MultiLineChart
                      key={`${series.key}-custom-${customRange.start.getTime()}-${customRange.end.getTime()}`}
                      startDate={customRange.start}
                      endDate={customRange.end}
                      color={series.color}
                      domain={series.domain}
                      tickValues={series.tickValues}
                      unit="MW"
                      tooltipUnit="MWh"
                      midlines={series.midlines}
                      wrapperClassName={series.wrapperClassName}
                      headerLeft={buildSeriesHeaderLeft(series.key, seriesAggregates[series.key])}
                      headerRight={buildLineSeriesHeaderRight(series.title, seriesAggregates[series.key]?.total)}
                      dataEndpoint={`/api/graph/portfolio/${portfolio.portfolio_name}/metrics/custom/${series.pathSuffix}?start=${start}&end=${end}`}
                      onAggregatesLoaded={handleSeriesAggregatesLoaded(series.key)}
                      onTooltip={setTooltip}
                      timeZone={dateFormatter.timeZone}
                    />
                  );
                })}
              {!isCustomRangeActive && (activeTab === 0 || activeTab === 1) &&
                SINGLE_LINE_SERIES.map((series) => {
                  const duration = activeTab === 0 ? '24hr' : '48hr';
                  return (
                    <MultiLineChart
                      key={`${series.key}-${duration}`}
                      duration={duration}
                      color={series.color}
                      domain={series.domain}
                      tickValues={series.tickValues}
                      unit="MW"
                      tooltipUnit="MWh"
                      midlines={series.midlines}
                      wrapperClassName={series.wrapperClassName}
                      headerLeft={buildSeriesHeaderLeft(series.key, seriesAggregates[series.key])}
                      headerRight={buildLineSeriesHeaderRight(series.title, seriesAggregates[series.key]?.total)}
                      dataEndpoint={`/api/graph/portfolio/${portfolio.portfolio_name}/metrics/${duration}/${series.pathSuffix}`}
                      onAggregatesLoaded={handleSeriesAggregatesLoaded(series.key)}
                      onTooltip={setTooltip}
                      timeZone={dateFormatter.timeZone}
                    />
                  );
                })}
              {!isCustomRangeActive && activeTab === 2 &&
                SINGLE_BAR_SERIES.map((series) => (
                    <SingleBarChart
                      key={`${series.key}-week`}
                      duration="week"
                      variant={series.variant}
                      gradient={series.gradient}
                      wrapperClassName={series.wrapperClassName}
                      headerLeft={buildSeriesHeaderLeft(series.key, seriesAggregates[series.key])}
                      headerRight={<strong>{series.title}</strong>}
                      tooltipUnit="MWh"
                      tooltipLabels={series.tooltipLabels}
                      dataEndpoint={`/api/graph/portfolio/${portfolio.portfolio_name}/metrics/week/${series.pathSuffix}`}
                      onAggregatesLoaded={handleSeriesAggregatesLoaded(series.key)}
                      domain={series.domain}
                      tickValues={series.tickValues}
                      fixedScale={series.fixedScale}
                      percentLine={series.percentLine}
                      onTooltip={setTooltip}
                      timeZone={dateFormatter.timeZone}
                    />
                ))}
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Dashboard